import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Image, Message, Modal } from 'semantic-ui-react';

import {
  closeImageModal,
  uploadImageWithMetadata,
} from '../../actions/image_modal/image_modal';
import { validateAltText } from '../../helpers/validations';

const messages = defineMessages({
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  altLabel: {
    id: 'imageAltLabel',
    defaultMessage: 'Alternative Text - Alt (required)*',
  },
  captionLabel: {
    id: 'imageCaptionLabel',
    defaultMessage: 'Caption',
  },
  description: {
    id: 'imageDescriptionLabel',
    defaultMessage: 'Description',
  },
  altPlaceholder: {
    id: 'Alt placeholder',
    defaultMessage: 'Describe the image for assistive technologies',
  },
  captionPlaceholder: {
    id: 'Caption placeholder',
    defaultMessage: 'Caption displayed below the image',
  },
});

/**
 * ImageUploadModal Component - Modal-First Image Upload Pattern (Multi-Image Support)
 *
 * GERENCIAMENTO DE ESTADO COMPLETO:
 *
 * Estados do Modal:
 * - CLOSED:    Arquivo = null, modal não visível
 * - OPEN:      Arquivo selecionado, aguardando input do usuário
 * - UPLOADING: Upload em progresso (isUploading=true)
 * - SUCCESS:   Upload completo (uploadedImage preenchido)
 * - ERROR:     Falha no upload (error preenchido)
 *
 * Props:
 *   open (boolean) - Modal está visível?
 *   file (File|File[]) - Arquivo único ou array de arquivos
 *   onClose (function) - Callback ao fechar
 *   path (string) - Caminho para upload
 *   onSuccess (function) - Callback com sucesso (chamado após cada upload)
 *   uploadContent (function) - Action do Volto (injetado)
 *   block (string) - ID do bloco (injetado)
 *   onComplete (function) - Callback ao completar TODAS as imagens
 *
 * Redux State (state.image_modal.modal):
 *   isUploading (boolean) - Upload em progresso?
 *   uploadedImage (object) - Dados da imagem criada (com @id)
 *   error (string) - Mensagem de erro ou null
 */
const ImageUploadModal = ({
  open,
  file,
  onClose,
  path,
  onSuccess,
  uploadContent,
  block,
  onComplete,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // ============ LOCAL STATE ============
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    alt: '',
    caption: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // ============ MULTI-IMAGE STATE ============
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const currentFile = files[currentFileIndex] || null;
  const totalFiles = files.length;
  const hasMultipleFiles = totalFiles > 1;
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // ============ REDUX STATE ============
  const isUploading = useSelector(
    (state) => state.image_modal?.modal?.isUploading,
  );
  const uploadedImage = useSelector(
    (state) => state.image_modal?.modal?.uploadedImage,
  );
  const uploadError = useSelector((state) => state.image_modal?.modal?.error);

  // ============ DERIVED STATE ============
  const getModalState = () => {
    if (!currentFile) return 'CLOSED';
    if (isUploading) return 'UPLOADING';
    if (uploadError) return 'ERROR';
    if (uploadedImage) return 'SUCCESS';
    return 'OPEN';
  };

  const modalState = getModalState();
  // Botão Enviar deve ficar habilitado, exceto quando processando/uploading
  const isReadyToUpload = !isUploading && !isProcessing;
  const ALT_FIELD_ID = 'image-upload-modal-alt-field';
  const isLastFile = currentFileIndex === totalFiles - 1;
  const canGoNext = !isUploading && !isProcessing;

  // ============ EFFECTS ============

  /**
   * Effect: Sincronizar arquivos recebidos via prop `file`
   * Sempre que `file` mudar, reinicia a fila local de arquivos
   */
  useEffect(() => {
    const array = Array.isArray(file) ? file : file ? [file] : [];
    setFiles(array);
    setCurrentFileIndex(0);
    setUploadedFiles([]);
  }, [file]);

  /**
   * Effect: Preview da imagem
   * Gera preview quando arquivo muda (navegação entre múltiplas)
   */
  useEffect(() => {
    if (!currentFile) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(currentFile);

    // Resetar form quando novo arquivo é selecionado
    setFormData({ title: '', description: '', alt: '', caption: '' });
    setFormErrors({});

    return () => {
      setImagePreview(null);
    };
  }, [currentFile, currentFileIndex]);

  /**
   * Effect: Sucesso de upload
   * Dispara quando upload completa com sucesso
   *
   * Sequência de operações:
   * 1. Chamar onSuccess com dados da imagem
   * 2. Se houver próxima imagem: avançar para ela
   * 3. Se foi a última: chamar onComplete e fechar
   * 4. Zerar Redux state
   * 5. Zerar local state (form, errors, preview)
   */
  useEffect(() => {
    if (modalState !== 'SUCCESS') return;

    const handleSuccess = async () => {
      setIsProcessing(true);

      try {
        // 1. Acumular imagem enviada e disparar callback imediato
        //    (single e multi-imagem): bloco/conteúdo é criado logo após
        //    o upload de cada arquivo
        if (uploadedImage) {
          setUploadedFiles((prev) => [...prev, uploadedImage]);

          if (onSuccess) {
            onSuccess(uploadedImage);
          }
        }

        // 2. Zerar Redux state
        dispatch(closeImageModal());

        // 3. Zerar local state
        setFormData({ title: '', description: '', alt: '', caption: '' });
        setFormErrors({});
        setImagePreview(null);

        // 4. Verificar se há próxima imagem
        await new Promise((resolve) => setTimeout(resolve, 500)); // Pequena pausa

        // Remover do array local a imagem já enviada
        setFiles((prev) =>
          prev.filter((_, index) => index !== currentFileIndex),
        );

        if (!isLastFile && canGoNext) {
          // Há próxima imagem na fila: o índice permanece apontando
          // para o próximo item após a remoção acima
        } else {
          // Era a última imagem: chamar onComplete (se definido)
          const allUploaded = [...uploadedFiles, uploadedImage].filter(Boolean);

          if (onComplete) {
            onComplete(allUploaded);
          }
          onClose();
        }
      } catch (err) {
        // Em caso de erro na callback, ainda assim limpar Redux
        dispatch(closeImageModal());
      } finally {
        setIsProcessing(false);
      }
    };

    handleSuccess();
  }, [
    modalState,
    uploadedImage,
    onSuccess,
    onComplete,
    onClose,
    dispatch,
    isLastFile,
    canGoNext,
    uploadedFiles,
    currentFile,
    currentFileIndex,
  ]);

  // ============ HANDLERS ============

  /**
   * Handler: Mudança nos campos do formulário
   */
  const handleInputChange = (e, { name, value }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validação em tempo real para alt
    if (name === 'alt') {
      const err = validateAltText(value);
      setFormErrors((prev) => ({
        ...prev,
        alt: err ? { content: err, pointing: 'below' } : null,
      }));
    }
  };

  /**
   * Handler: Botão "Enviar Imagem"
   * Valida form e dispara action de upload
   */
  const handleUpload = () => {
    const altError = validateAltText(formData.alt);
    if (altError) {
      setFormErrors({ alt: { content: altError, pointing: 'below' } });
      // Focar no campo ALT e rolar para ele
      setTimeout(() => {
        const el = document.getElementById(ALT_FIELD_ID);
        if (el) {
          el.focus();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
      return;
    }

    dispatch(
      uploadImageWithMetadata(
        path,
        currentFile,
        formData,
        uploadContent,
        block,
      ),
    );
  };

  /**
   * Handler: Fechamento do modal
   * Limpa tudo (local + Redux) e permite novo upload
   * Não permite fechar enquanto upload está em progresso
   */
  const handleModalClose = () => {
    if (isUploading || isProcessing) return;

    setFormData({ title: '', description: '', alt: '', caption: '' });
    setFormErrors({});
    setImagePreview(null);
    setCurrentFileIndex(0);
    setUploadedFiles([]);
    dispatch(closeImageModal());
    onClose();
  };

  /**
   * Handler: Tentar novamente após erro
   */
  const handleRetry = () => {
    dispatch(closeImageModal());
    setFormData({ title: '', description: '', alt: '', caption: '' });
    setFormErrors({});
  };

  // ============ RENDER ============

  if (!currentFile) return null;

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      closeOnDimmerClick={!isUploading && !isProcessing}
      closeOnEscape={!isUploading && !isProcessing}
      size="small"
      className="image-upload-modal"
    >
      {/* HEADER */}
      <Modal.Header>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <FormattedMessage
              id="Prepare Image for Upload"
              defaultMessage="Prepare Image for Upload"
            />
            {modalState === 'UPLOADING' && (
              <>
                {' - '}
                <FormattedMessage
                  id="Uploading"
                  defaultMessage="Uploading..."
                />
              </>
            )}
            {modalState === 'ERROR' && (
              <>
                {' - '}
                <FormattedMessage id="Error" defaultMessage="Error" />
              </>
            )}
            {modalState === 'SUCCESS' && (
              <>
                {' - '}
                <FormattedMessage id="Success!" defaultMessage="Success!" />
              </>
            )}
          </div>
          {hasMultipleFiles && (
            <div
              style={{
                fontSize: '0.9em',
                color: '#666',
                fontWeight: 'normal',
              }}
            >
              {currentFileIndex + 1} / {totalFiles}
            </div>
          )}
        </div>
      </Modal.Header>

      {/* CONTENT */}
      <Modal.Content scrolling>
        {/* Preview - mostrar apenas nos estados OPEN e UPLOADING */}
        {imagePreview &&
          (modalState === 'OPEN' || modalState === 'UPLOADING') && (
            <div
              style={{
                marginBottom: '1.5rem',
                textAlign: 'center',
                padding: '1rem',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>
                <FormattedMessage
                  id="Image Preview"
                  defaultMessage="Image Preview"
                />
              </h4>
              <Image
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  margin: '0 auto',
                  display: 'block',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            </div>
          )}

        {/* Info do arquivo - mostrar apenas nos estados OPEN e UPLOADING */}
        {(modalState === 'OPEN' || modalState === 'UPLOADING') && (
          <Message info>
            <Message.Header>
              <FormattedMessage
                id="Selected file"
                defaultMessage="Selected file:"
              />
              {hasMultipleFiles && ` (${currentFileIndex + 1}/${totalFiles})`}
            </Message.Header>
            <p>
              <strong>{currentFile.name}</strong> (
              {(currentFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          </Message>
        )}

        {/* Formulário - apenas no estado OPEN */}
        {modalState === 'OPEN' && (
          <Form>
            <Form.Input
              label={<FormattedMessage id="Title" defaultMessage="Title" />}
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Ex: Meeting with Ministers"
              maxLength={255}
            />

            <Form.TextArea
              label={<FormattedMessage {...messages.description} />}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the context and content of the image..."
              rows={3}
              maxLength={1000}
            />
            <div
              style={{
                fontSize: '0.85em',
                color: '#999',
                marginBottom: '1rem',
              }}
            >
              {formData.description.length}/1000{' '}
              <FormattedMessage id="characters" defaultMessage="characters" />
            </div>

            <Form.TextArea
              label={<FormattedMessage {...messages.altLabel} />}
              name="alt"
              value={formData.alt}
              onChange={handleInputChange}
              error={formErrors.alt}
              placeholder={intl.formatMessage(messages.altPlaceholder)}
              maxLength={100}
              rows={2}
              required
              id={ALT_FIELD_ID}
              aria-required="true"
              aria-invalid={!!formErrors.alt}
            />
            <div
              style={{
                fontSize: '0.85em',
                color: '#999',
                marginBottom: '1rem',
              }}
            >
              {formData.alt.length}/100{' '}
              <FormattedMessage id="characters" defaultMessage="characters" /> (
              <FormattedMessage id="minimum 3" defaultMessage="minimum 3" />)
            </div>

            <Form.TextArea
              label={<FormattedMessage {...messages.captionLabel} />}
              name="caption"
              value={formData.caption}
              onChange={handleInputChange}
              placeholder={intl.formatMessage(messages.captionPlaceholder)}
              maxLength={500}
              rows={2}
            />
            <div style={{ fontSize: '0.85em', color: '#999' }}>
              {formData.caption.length}/500{' '}
              <FormattedMessage id="characters" defaultMessage="characters" />
            </div>
          </Form>
        )}

        {/* Tips - only in OPEN state */}
        {modalState === 'OPEN' && (
          <Message warning style={{ marginTop: '1rem' }}>
            <Message.Header>
              <FormattedMessage
                id="Tips for alt text"
                defaultMessage="Tips for valid alternative text - alt:"
              />
            </Message.Header>
            <ul style={{ marginBottom: 0 }}>
              <li>
                ✅{' '}
                <FormattedMessage
                  id="Describe content 3-160"
                  defaultMessage="Describe the content in 3-160 characters"
                />
              </li>
              <li>
                ❌{' '}
                <FormattedMessage
                  id="Avoid photo image"
                  defaultMessage='Avoid: "photo", "image", "IMG_123"'
                />
              </li>
              <li>
                ❌{' '}
                <FormattedMessage
                  id="Do not include extension"
                  defaultMessage="Do not include extension (.jpg, .png, .webp)"
                />
              </li>
            </ul>
          </Message>
        )}

        {/* Upload indicator - UPLOADING state */}
        {modalState === 'UPLOADING' && (
          <Message icon>
            <Message.Content>
              <Message.Header>
                <FormattedMessage
                  id="Sending image"
                  defaultMessage="Sending image..."
                />
              </Message.Header>
              <p>
                <FormattedMessage
                  id="Please wait"
                  defaultMessage="Please wait."
                />
              </p>
            </Message.Content>
          </Message>
        )}

        {/* Success message - SUCCESS state */}
        {modalState === 'SUCCESS' && (
          <Message
            success
            icon="check circle"
            header={
              <FormattedMessage id="Success!" defaultMessage="Success!" />
            }
            content={
              isLastFile ? (
                <FormattedMessage
                  id="Image sent successfully closing"
                  defaultMessage="Image sent successfully. Closing modal..."
                />
              ) : (
                <FormattedMessage
                  id="Image sent successfully next"
                  defaultMessage="Image sent successfully. Moving to the next image..."
                />
              )
            }
          />
        )}

        {/* Error message - ERROR state */}
        {modalState === 'ERROR' && uploadError && (
          <Message
            error
            icon="warning circle"
            header={
              <FormattedMessage
                id="Error sending"
                defaultMessage="Error sending"
              />
            }
            content={uploadError}
          />
        )}
      </Modal.Content>

      {/* ACTIONS/FOOTER */}
      <Modal.Actions>
        {/* OPEN and UPLOADING states: Cancel + Navigate (if multiple) + Send */}
        {(modalState === 'OPEN' || modalState === 'UPLOADING') && (
          <>
            <Button onClick={handleModalClose} disabled={isUploading}>
              <FormattedMessage {...messages.cancel} />
            </Button>

            {/* Manual navigation between multiple images removed:
                the modal advances automatically as each upload
                is completed, and already sent images exit the queue. */}

            {modalState === 'OPEN' && (
              <Button
                primary
                onClick={handleUpload}
                disabled={!isReadyToUpload}
              >
                <FormattedMessage id="Send Image" defaultMessage="Send Image" />
              </Button>
            )}
          </>
        )}

        {/* SUCCESS state: only Done on the last image
            (on previous ones the advance is automatic via effect) */}
        {modalState === 'SUCCESS' && isLastFile && (
          <Button primary onClick={handleModalClose} disabled={isProcessing}>
            <FormattedMessage id="Done" defaultMessage="Done" />
          </Button>
        )}

        {/* ERROR state: Try Again + Close */}
        {modalState === 'ERROR' && (
          <>
            <Button onClick={handleModalClose}>
              <FormattedMessage id="Close" defaultMessage="Close" />
            </Button>
            <Button primary onClick={handleRetry}>
              <FormattedMessage id="Try Again" defaultMessage="Try Again" />
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default ImageUploadModal;
